import MuiLink from '@mui/material/Link';
import { formatClockText } from '../../../../../platform/util/clockSystem';
import { getBookingDisplayClock } from '../../../../../platform/util/bookingCalendarLabel';
// BookingHeader.jsx
import React from "react";
import { Avatar, Box, Chip, IconButton, Typography } from "@mui/material";
import { ArrowBack, CalendarMonth, Event, LocationOn, Person, Phone } from "@mui/icons-material";
import { parse, isValid, format, addMinutes } from "date-fns";
import { StyledBox, StyledTypography } from "../../../../../platform/assets/css/styledComponent";
import { formatDuration } from "../../../../../platform/util/utils";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { displayBookingDate, getBookingDisplayCalendar } from '../../../../../platform/util/bookingCalendarLabel';

// Updated getFormattedTimeRange function with a guard for undefined input
export function getFormattedTimeRange(time, length) {
  if (!time) {
    console.error("No time provided to getFormattedTimeRange");
    return "Invalid time";
  }
  
  let parsedDate;
  if (typeof time === "string") {
    const normalizedTime = time.toUpperCase();
    parsedDate = parse(normalizedTime, "yyyy-MM-dd'T'h:mma", new Date());
  } else if (time instanceof Date) {
    parsedDate = time;
  } else {
    console.error("Invalid time type:", time);
    return "Invalid time";
  }

  if (!isValid(parsedDate)) {
    console.error("Parsed date is invalid:", parsedDate);
    return "Invalid time";
  }

  const startTime = format(parsedDate, "h:mm a").toLowerCase();
  const endTime = format(addMinutes(parsedDate, length), "h:mm a").toLowerCase();

  return `${startTime} - ${endTime}`;
}

const BookingHeader = ({ bookingData, parsedTime, storedTimeZone, staff, navigate }) => { 
  // Guard against undefined parsedTime
  if (!parsedTime || !isValid(parsedTime)) {
    console.error("BookingHeader received an invalid parsedTime:", parsedTime);
    return (
      <StyledBox sx={{ padding: "20px" }}>
        <StyledTypography variant="h6">Invalid booking time</StyledTypography>
      </StyledBox>
    );
  }

  // Use getFormattedTimeRange with the provided parsedTime (which is a Date)
  const timeRange = formatClockText(getFormattedTimeRange(parsedTime, bookingData.length), getBookingDisplayClock(bookingData.calendarSystem));
  const date = isValid(parsedTime) ? displayBookingDate(format(parsedTime, 'yyyy-MM-dd'), getBookingDisplayCalendar(bookingData.calendarSystem)) : 'Invalid date';
  const providerName = staff?.user?.username || staff?.name || "Host";
  const providerImageUrl = staff?.image?.url || null;
  const detailRows = [
    {
      icon: <CalendarMonth sx={{ fontSize: 18, color: "primary.main" }} />,
      label: "Time",
      value: (
        <>
          {timeRange} <strong>({formatDuration(bookingData.length)})</strong>
        </>
      ),
    },
    {
      icon: <Event sx={{ fontSize: 18, color: "primary.main" }} />,
      label: "Date",
      value: date,
    },
    bookingData.location
      ? {
          icon: <LocationOn sx={{ fontSize: 18, color: "primary.main" }} />,
          label: "Location",
          value: bookingData.location,
        }
      : null,
    Number(bookingData.meetingLocationTypeId) === 2
      ? {
          icon: <Phone sx={{ fontSize: 18, color: "primary.main" }} />,
          label: "Phone details",
          value: bookingData.canInviteeCall
            ? (bookingData.phoneNumber?.trim()
              ? <>Call <MuiLink href={`tel:${bookingData.phoneNumber.replace(/[^+\d]/g, "")}`} color="text.primary" underline="always" sx={{ fontWeight: 500, textUnderlineOffset: "3px", textDecorationColor: "text.secondary", overflowWrap: "anywhere", "&:hover": { textDecorationColor: "currentColor" }, "&:focus-visible": { outline: "2px solid currentColor", outlineOffset: "3px", borderRadius: "2px" } }}>{bookingData.phoneNumber}</MuiLink> at the scheduled time.</>
              : "Contact the host for the phone number.")
            : "We'll call you at the phone number you provide.",
        }
      : null,
    bookingData?.price !== undefined &&
    bookingData?.price !== null &&
    String(bookingData?.price).trim() !== ""
      ? {
          icon: <AttachMoneyIcon sx={{ fontSize: 18, color: "primary.main" }} />,
          label: "Price",
          value: Number(bookingData?.price) === 0 ? "Free" : `$ ${bookingData.price}`,
        }
      : null,
    {
      icon: providerImageUrl ? (
        <Avatar src={providerImageUrl} alt={providerName} sx={{ width: 30, height: 30 }} />
      ) : (
        <Person sx={{ fontSize: 18, color: "primary.main" }} />
      ),
      label: "Host",
      value: providerName,
    },
    {
      icon: <Event sx={{ fontSize: 18, color: "primary.main" }} />,
      label: "Timezone",
      value: storedTimeZone,
    },
  ].filter(Boolean);

  return (
    <StyledBox
      sx={{
        flex: { xs: "1 1 100%", md: "0 0 36%" },
        minWidth: 0,
        padding: { xs: "20px", sm: "24px" },
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        bgcolor: "background.paper",
        color: "text.primary",
      }}
    >
      <StyledBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            width: 42,
            height: 42,
            border: 1, borderColor: "divider",
            backgroundColor: "background.paper",
            color: "text.primary",
            boxShadow: "0 10px 24px rgba(15,31,46,0.06)",
            "&:hover": {
              backgroundColor: "background.paper",
              borderColor: "rgba(29,78,216,0.22)",
            },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Chip
          label="Appointment Details"
          size="small"
          sx={{
            height: 28,
            borderRadius: "999px",
            backgroundColor: "action.selected",
            color: "primary.main",
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: "0.02em",
          }}
        />
      </StyledBox>
      <Box>
        <Typography
          variant="overline"
          sx={{
            display: "block",
            color: "text.secondary",
            letterSpacing: "0.12em",
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Appointment
        </Typography>
        <StyledTypography
          fontSize="29px"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.25,
            overflowWrap: "break-word",
          }}
        >
        {bookingData.title}
        </StyledTypography>
        <Typography variant="body2" sx={{ mt: 1.25, color: "text.secondary", lineHeight: 1.7 }}>
          Review your details, then confirm your booking.
        </Typography>
      </Box>
      <StyledBox sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
        {detailRows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.4,
              p: 1.5,
              borderRadius: 2.5,
              border: 1, borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "action.selected",
                flexShrink: 0,
              }}
            >
              {row.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 700,
                  letterSpacing: "0.035em",
                  fontSize: "0.68rem",
                }}
              >
                {row.label}
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  color: "text.primary",
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                {row.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </StyledBox>
    </StyledBox>
  );
};

export default BookingHeader;
